import React, {FC} from "react"
import {getActiveProducts} from "@irati/products/utils/products"
import {injectIntl, InjectIntl} from "@irati/utils/lang"
import {PerkboxLogo} from "@irati/ui/src/Logos"
import PropTypes from "prop-types"
import Flex from "@irati/ui/src/Flex"
import {H4, H5, Label} from "@irati/ui/src/Typography"
import Products from "@irati/products/enums/Products"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import tracking from "@irati-tracking/platform"
import {
  cultureHubNotificationsCountVariables,
  recognitionNotificationsCountVariables,
  useNotificationCountQuery
} from "@irati/notifications/hooks"
import Pill from "@irati/ui/src/Pill"
import mainTheme from "@irati/ui/src/global/theme"
import useAuth from "@irati/auth/helpers/auth/useAuth"

import {NewPillWrapper} from "../styled"
import getProductDetails from "../utils/getProductDetails"
import {StyledBusinessLogoWrapper, StyledLogo, StyledRouterLink} from "./styled"

const MAX_NOTIFICATIONS_COUNT = 99

interface Context {
  apps: {[key: string]: string}
  appPath: string
}

interface Props extends InjectIntl {
  onClick?: () => void
  isSidebarNavigation?: boolean
  businessLogoUrl?: string
}

const getIconForProduct = (product: Products, isSelected: boolean) => {
  switch (product) {
    case Products.perks:
      return isSelected ? IconTypes.perksFill : IconTypes.perks
    case Products.recognition:
      return IconTypes.celebrationAnimated
    case Products.rewards:
      return isSelected ? IconTypes.celebrationFill : IconTypes.celebration
    case Products.medical:
      return isSelected ? IconTypes.medicalFill : IconTypes.medical
    case Products.wellness:
      return isSelected ? IconTypes.wellnessFill : IconTypes.wellness
    case Products.cultureHub:
      return isSelected ? IconTypes.cultureHubFill : IconTypes.cultureHub
    case Products.ctw:
    case Products.cycleToWork:
      return isSelected ? IconTypes.cycleToWorkFill : IconTypes.cycleToWork
    case Products.he:
    case Products.homeAndElectronics:
      return isSelected ? IconTypes.homeElectronicsFill : IconTypes.homeElectronics
    case Products.alp:
    case Products.annualLeavePurchase:
      return isSelected ? IconTypes.annualLeavePurchaseFill : IconTypes.annualLeavePurchase
    case Products.onlineGP:
      return isSelected ? IconTypes.medicalFill : IconTypes.medical
  }
}
const NotificationPillCounter = ({count}: {count: number}) => {
  if (!count) {
    return null
  }
  const pillCount = count > MAX_NOTIFICATIONS_COUNT ? `${MAX_NOTIFICATIONS_COUNT}+` : `+${count}`
  return (
    <NewPillWrapper>
      <Pill text={pillCount} backgroundColor={mainTheme.colors.tertiary.r_400} isWhiteText />
    </NewPillWrapper>
  )
}
const NavigationMenuContent: FC<Props> = ({formatMessage, onClick, isSidebarNavigation, businessLogoUrl, match}, context: Context) => {
  const {apps, appPath} = context
  const benefitsEnabled = useAuth().productPermissions?.benefits
  const activeProducts = getActiveProducts()
  const {data: celebrationHubNotificationCount} = useNotificationCountQuery(recognitionNotificationsCountVariables)
  const {data: cultureHubNotificationCount} = useNotificationCountQuery(cultureHubNotificationsCountVariables)

  const {HOME} = apps
  const isHomeSelected = appPath === HOME
  const handleOnClick = async (trackingName: string, productName?: Products) => {
    const isHighlighted = productName === Products.recognition && !!celebrationHubNotificationCount
    tracking.click("homepage_card", {
      product: trackingName,
      is_highlighted: isHighlighted,
      active: true,
      source: "menu"
    })
    if (onClick) {
      onClick()
    }
  }

  const TitleWrapper = isSidebarNavigation ? H5 : H4
  return (
    <>
      {isSidebarNavigation && businessLogoUrl && (
        <StyledBusinessLogoWrapper>
          <StyledLogo src={businessLogoUrl} />
        </StyledBusinessLogoWrapper>
      )}
      <Flex direction="column" justifyContent="space-between" flex="1" padding={[2, 2, 0, 2]}>
        <Flex direction="column" gap={benefitsEnabled ? 1 : 4}>
          <StyledRouterLink
            isSidebarNavigation={isSidebarNavigation}
            id="HOME_LINK"
            isSelected={isHomeSelected}
            app={HOME}
            to="/"
            onClick={() => handleOnClick("home")}>
            <Icon icon={isHomeSelected ? IconTypes.homeFill : IconTypes.home} size={24} />
            <TitleWrapper marginLeft={2} weight="bold">
              {formatMessage({id: "platform.home.title"})}
            </TitleWrapper>
          </StyledRouterLink>
          {activeProducts.map(({product, trackingName}, index) => {
            const productDetails = getProductDetails(formatMessage)[product]
            const app = productDetails?.app || apps[product]
            const to = productDetails?.to || "/"
            const isSelected = productDetails?.app ? window.location.pathname === `${app}/${to}` : appPath === apps[product]

            return productDetails ? (
              <StyledRouterLink
                isSidebarNavigation={isSidebarNavigation}
                key={index}
                id={`${product}_LINK`}
                isSelected={isSelected}
                app={app}
                to={to}
                onClick={() => handleOnClick(trackingName, product)}>
                <Icon icon={getIconForProduct(product, isSelected)} size={24} />
                <TitleWrapper marginLeft={2} weight="bold">
                  {productDetails.title}
                </TitleWrapper>
                {product === Products.recognition && <NotificationPillCounter count={celebrationHubNotificationCount} />}
                {product === Products.cultureHub && <NotificationPillCounter count={cultureHubNotificationCount} />}
              </StyledRouterLink>
            ) : null
          })}
        </Flex>
        <Flex direction="column" alignItems="flex-start" marginBottom={3}>
          <Label weight="bold" small marginBottom>
            {formatMessage({id: "platform.ui.poweredBy"})}
          </Label>
          <PerkboxLogo size={28} />
        </Flex>
      </Flex>
    </>
  )
}

NavigationMenuContent.contextTypes = {
  apps: PropTypes.object.isRequired,
  appPath: PropTypes.string.isRequired
}

export default injectIntl(NavigationMenuContent)
